import React, { useCallback } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { useTranslation } from 'react-i18next';

import { useMytaverseEvent } from '@core/dashboard/providers';

import { KIND_TOKENS, MODE_ICON, MODE_META, modeForEntity } from './kind';
import type { AiEntity } from './types';
import { useAiInspectorStyles, KIND_LABEL_KEY } from './inspectorStyles';

interface IAiInspectorHeaderProps {
  entity: AiEntity;
  onClose: () => void;
}

const AiInspectorHeader = ({ entity, onClose }: IAiInspectorHeaderProps) => {
  const { t: translate } = useTranslation();
  const classes = useAiInspectorStyles();
  const { requestAssistantOpen } = useMytaverseEvent();

  const mode = modeForEntity(entity);
  const tokens = KIND_TOKENS[mode];
  const ModeIcon = MODE_ICON[mode];
  const meta = MODE_META[mode];
  // Trigger/Spot get the colour-coded chip; knowledge falls back to a neutral
  // label so the panel still reads clearly.
  const isColored = mode !== 'knowledge';
  const chipLabel = meta.chipKey
    ? translate(meta.chipKey, meta.chipFallback ?? '')
    : translate(
        KIND_LABEL_KEY[entity.kind].key,
        KIND_LABEL_KEY[entity.kind].fallback,
      );

  const handleOpenAssistant = useCallback(() => {
    // Mirrors Inspector.tsx — closing the inspector unmounts it; the wrapper
    // sets `isEditorInspectorOpen=false`, then the nonce bump pops Noa open.
    onClose();
    requestAssistantOpen();
  }, [onClose, requestAssistantOpen]);

  const handleCopy = useCallback(async () => {
    if (!entity.primaryTrigger) {
      return;
    }
    try {
      await navigator.clipboard.writeText(entity.primaryTrigger);
    } catch (copyError) {
      console.warn(
        `Failed to copy identifier: ${(copyError as Error).message}`,
        { trigger: entity.primaryTrigger },
      );
    }
  }, [entity.primaryTrigger]);

  return (
    <Box sx={classes.header}>
      <Box
        sx={[
          classes.headerIcon,
          isColored && {
            backgroundColor: tokens.bg,
            color: tokens.main,
          },
        ]}
      >
        <ModeIcon />
      </Box>
      <Box sx={classes.headerTitleColumn}>
        <Tooltip title={entity.name} placement="bottom" enterDelay={400}>
          <Typography sx={classes.headerTitle}>{entity.name}</Typography>
        </Tooltip>
        <Box sx={classes.headerSubRow}>
          {entity.primaryTrigger ? (
            <Tooltip
              title={entity.primaryTrigger}
              placement="bottom"
              enterDelay={400}
            >
              <Typography component="span" sx={classes.monoIdChip}>
                {entity.primaryTrigger}
                <IconButton
                  size="small"
                  sx={classes.copyButton}
                  onClick={handleCopy}
                  aria-label={translate(
                    'editorMode.copyIdentifier',
                    'Copy identifier',
                  )}
                >
                  <ContentCopyOutlinedIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Typography>
            </Tooltip>
          ) : null}
          <Box
            component="span"
            sx={[
              classes.kindChip,
              isColored && {
                color: tokens.text,
                borderColor: tokens.border,
                backgroundColor: tokens.bg,
                '& svg': { color: tokens.main, fontSize: 11 },
              },
            ]}
          >
            <ModeIcon sx={{ fontSize: 11 }} />
            {chipLabel}
          </Box>
          {entity.aliases.length > 0 ? (
            <Tooltip
              title={entity.aliases.join(', ')}
              placement="bottom"
              enterDelay={300}
            >
              <Box component="span" sx={classes.aliasChip}>
                {translate(
                  'editorMode.aiAliasChip',
                  '+{{count}} alias',
                  { count: entity.aliases.length },
                )}
              </Box>
            </Tooltip>
          ) : null}
        </Box>
      </Box>
      <Tooltip
        title={translate(
          'editorMode.openVirtualAssistant',
          'Open virtual assistant',
        )}
        placement="bottom"
        enterDelay={400}
      >
        <IconButton
          size="small"
          sx={classes.closeButton}
          onClick={handleOpenAssistant}
          aria-label={translate(
            'editorMode.openVirtualAssistant',
            'Open virtual assistant',
          )}
        >
          <SmartToyOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        sx={classes.closeButton}
        onClick={onClose}
        aria-label={translate('editorMode.close', 'Close')}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default AiInspectorHeader;
